import { useState } from 'react';
import { MdClose, MdFilterList, MdExpandMore, MdExpandLess, MdArrowForward } from 'react-icons/md';
import StudentSelect from '../common/StudentSelect';
import DateField from '../common/DateField';
import useClubVocab from '../../hooks/useClubVocab';

const CATEGORY_OPTIONS = [
  ['', 'All categories'],
  ['technique', 'Technique'],
  ['attendance', 'Attendance'],
  ['belt_readiness', 'Belt Readiness'],
  ['behavior', 'Behavior'],
  ['injury', 'Injury'],
  ['safety', 'Safety'],
  ['general', 'General'],
  ['other', 'Other'],
];

const TYPE_TABS = [
  ['', 'All', 'bg-secondary text-card border-secondary'],
  ['progress_note', 'Progress', 'bg-secondary text-card border-secondary'],
  ['incident_report', 'Incident', 'bg-secondary text-card border-secondary'],
];

function RecordFilters({ filters, onFilterChange, students = [], onClear }) {
  const v = useClubVocab();
  const [open, setOpen] = useState(false);

  const set = (key, value) => onFilterChange({ ...filters, [key]: value, page: 1 });
  // Prefer the page-level clear (shared with the empty-state "Clear filters"); fall
  // back to a local reset if no handler is passed.
  const clearAll = onClear || (() => onFilterChange({
    studentId: '', recordType: '', category: '', severity: '',
    createdBy: '', dateFrom: '', dateTo: '', page: 1,
  }));

  // Count of advanced filters (those hidden behind the toggle); Type lives inline.
  const advancedCount = [filters.studentId, filters.category, filters.severity, filters.dateFrom, filters.dateTo]
    .filter(Boolean).length;
  const anyActive = advancedCount > 0 || !!filters.recordType;

  const fieldLabel = 'block text-xs font-semibold text-muted mb-1.5';
  const selectCls  = 'w-full appearance-none bg-card border border-divider rounded-full pl-3 pr-9 py-2 text-sm text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-secondary transition-colors';

  return (
    <div className="py-3 space-y-3">
      {/* Compact bar: Type segmented + Filters toggle + Clear all */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          {TYPE_TABS.map(([val, lbl, active]) => (
            <button
              key={val}
              onClick={() => set('recordType', val)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                (filters.recordType || '') === val ? active : 'bg-card text-muted border-divider hover:text-secondary'
              }`}
            >
              {lbl}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {anyActive && (
            <button
              onClick={clearAll}
              className="text-xs font-medium text-muted hover:text-secondary flex items-center gap-1 transition-colors"
            >
              <MdClose className="text-sm" /> Clear all
            </button>
          )}
          <button
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-divider text-secondary hover:bg-nav-hover transition-colors"
          >
            <MdFilterList className="text-base" /> Filters
            {advancedCount > 0 && (
              <span className="ml-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-primary text-white text-[10px] font-semibold leading-none">
                {advancedCount}
              </span>
            )}
            {open ? <MdExpandLess className="text-base" /> : <MdExpandMore className="text-base" />}
          </button>
        </div>
      </div>

      {/* Expanded advanced filters */}
      {open && (
        <div className="bg-card border border-divider rounded-2xl p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Student — searchable */}
            <div>
              <label className={fieldLabel}>{v.member}</label>
              <StudentSelect
                students={students}
                value={filters.studentId || ''}
                onChange={(id) => set('studentId', id)}
                allowAll
                allOptionLabel={`All ${v.members.toLowerCase()}`}
              />
            </div>

            {/* Category */}
            <div>
              <label className={fieldLabel}>Category</label>
              <div className="relative">
                <select value={filters.category || ''} onChange={(e) => set('category', e.target.value)} className={selectCls}>
                  {CATEGORY_OPTIONS.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
                </select>
                <MdExpandMore className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted text-lg pointer-events-none" />
              </div>
            </div>

            {/* Severity */}
            <div>
              <label className={fieldLabel}>Severity</label>
              <div className="relative">
                <select value={filters.severity || ''} onChange={(e) => set('severity', e.target.value)} className={selectCls}>
                  <option value="">All severities</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
                <MdExpandMore className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted text-lg pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Date range — compact triggers that open a BS-aware picker in a popover.
              Empty = no bound; the × clears that bound (same AD ISO values as before). */}
          <div>
            <label className={fieldLabel}>Date range</label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="flex-1 min-w-0">
                <DateField
                  label="From date"
                  placeholder="Any date"
                  value={filters.dateFrom || ''}
                  onChange={(v) => set('dateFrom', v)}
                />
              </div>
              <MdArrowForward className="hidden sm:block text-subtle text-base flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <DateField
                  label="To date"
                  placeholder="Any date"
                  value={filters.dateTo || ''}
                  onChange={(v) => set('dateTo', v)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecordFilters;
